import { useNavigate } from 'react-router-dom';

import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUser, { isUserSubscribed } from '../authentication/context/useUser';
import SubscriptionModal from '../subscription/SubscriptionModal';
import BaseBlock, { TitleBlock } from '../ui/Block';
import { PageContainer } from '../ui/Container';
import NewButton from '../ui/NewButton';
import useReports from './context/useReports';
import CreateReportModal from './CreateReportModal';
import ReportCard from './ReportCard';

const Block = styled(BaseBlock)`
  padding: 16px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: flex-start;
  background-color: var(--lightgrey);
`;
const ReportTitleBlock = styled(TitleBlock)`
  position: relative;
  ::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 0;
    border-bottom: 16px solid white;
    border-right: 16px solid transparent;
  }
`;

const Results = () => {
  const { t } = useTranslation([
    'dashboard',
    'common',
    'annual-reports',
    'model',
  ]);
  const navigate = useNavigate();
  const { reports, remove } = useReports();
  const [openCreateReportModal, setOpenCreateReportModal] = useState(false);
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const { user } = useUser();

  const subscribed = isUserSubscribed(user);

  useEffect(() => {
    if (user && !subscribed) {
      setOpenSubscriptionModal(true);
    }
  }, [user]);

  if (!reports) return null;

  const handleNewProjectClicked = () => {
    setOpenCreateReportModal(true);
  };

  const handleReportClicked = (reportId: string) => () => {
    navigate(`/annual-reports/${reportId}`);
  };

  const handleReportDeleted = (reportId: string) => async (event: any) => {
    return await remove(reportId);
  };

  return (
    <PageContainer bgColor="white">
      <ReportTitleBlock background="var(--color-primary)">
        <h3>{t('annual-reports:title')}</h3>
        <p>{t('annual-reports:subtitle')}</p>
      </ReportTitleBlock>
      {openSubscriptionModal && (
        <SubscriptionModal
          open={openSubscriptionModal}
          onClose={() => setOpenSubscriptionModal(false)}
          title={t('common:subscription.annualReports.modalTitle')}
          text={t('common:subscription.annualReports.modalText')}
          link={t('common:subscription.annualReports.konwMoreLink')}
        />
      )}
      {openCreateReportModal && subscribed && (
        <CreateReportModal
          open={openCreateReportModal}
          setOpen={setOpenCreateReportModal}
          onClose={() => {
            setOpenCreateReportModal(false);
          }}
        />
      )}
      <Block>
        {subscribed && (
          <NewButton
            onClick={handleNewProjectClicked}
            createdObjectLabel={t('annual-reports:add-report')}
            bgColor="var(--green)"
          />
        )}
        {reports.map((report) => (
          <ReportCard
            report={report}
            key={report._id.toString()}
            onClick={handleReportClicked(report._id.toString())}
            onDelete={handleReportDeleted(report._id.toString())}
          />
        ))}
      </Block>
    </PageContainer>
  );
};

export default Results;
